/**
 * CoachingSessionCard
 * Displays a coaching session card matching Figma "Cards / Session Paid + Stats"
 * 640x468px, cover image with rounded corners, price badge, stats bar at bottom
 */

import {
  Banknote,
  Bookmark,
  Calendar,
  CircleCheck,
  Eye,
  MoreVertical,
  Pencil,
  Trash2,
  Upload,
  Users,
} from "lucide-react"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { ShareModal } from "@/components/shared/ShareModal"
import { TagChip } from "@/components/shared/TagChip"
import { Avatar } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { CoachingBookSessionModal } from "@/features/coaching/components/CoachingBookSessionModal"
import { incrementCoachingViewCount } from "@/features/coaching/services/coaching.service"

export interface CoachingSessionCardProps {
  id: string
  title: string
  coverImage?: string | null
  format: string
  duration: number
  durationType?: "minutes" | "hours" | "days"
  participationFee: string
  price?: number | null
  description?: string | null
  tags?: string[]
  location?: string | null
  websiteLink?: string | null
  creator?: {
    id: string
    name: string | null
    username: string | null
    profilePictureUrl?: string | null
    followers?: number
  } | null
  isOwner?: boolean
  isBookmarked?: boolean
  onBookmarkClick?: () => void
  onEditClick?: () => void
  onDeleteClick?: () => void
  onCalendarClick?: () => void
  onBookSession?: () => void
  stats?: {
    views: number
    bookings: number
    completed: number
    earning: number
  } | null
}

export function CoachingSessionCard({
  id,
  title,
  coverImage,
  format,
  duration,
  durationType = "minutes",
  participationFee,
  price,
  description,
  tags,
  creator,
  isOwner = false,
  isBookmarked,
  onBookmarkClick,
  onEditClick,
  onDeleteClick,
  onCalendarClick,
  onBookSession,
  location,
  websiteLink,
  stats,
}: CoachingSessionCardProps) {
  const navigate = useNavigate()
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false)
  const [shareOpen, setShareOpen] = useState(false)

  const isPaid = participationFee === "Paid"
  const priceNum = price ? Number.parseFloat(String(price)) : 0
  const priceLabel = isPaid && priceNum > 0 ? `$${Math.round(priceNum)}` : "Free"
  // Duration is stored in minutes already; no conversion needed
  const durationLabel =
    duration >= 60
      ? duration % 60 === 0
        ? `${Math.floor(duration / 60)} hr`
        : `${Math.floor(duration / 60)} hr ${duration % 60} min`
      : `${duration} min`

  const handleBookSessionClick = async () => {
    // Increment view count when Book a Session is clicked
    try {
      await incrementCoachingViewCount(id)
    } catch (error) {
      // Silently fail - view count is not critical for user experience
      console.warn("Failed to increment view count:", error)
    }

    if (onBookSession) {
      onBookSession()
    } else {
      // If no callback provided, open the modal directly
      setIsBookingModalOpen(true)
    }
  }

  return (
    <div className="relative max-w-[640px] h-full w-full overflow-hidden rounded-tl-[40px] rounded-br-[40px] border border-white/10 bg-white/5 flex flex-col">
      {/* Cover Image */}
      <div className="relative h-[220px] md:h-[340px] w-full overflow-hidden rounded-tl-[40px] bg-brand-slate shrink-0">
        {coverImage ? (
          <img
            src={coverImage}
            alt={title}
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 bg-brand-slate" />
        )}
        <div className="absolute right-5 top-5 flex items-center gap-2">
          {/* Admin Controls */}
          {isOwner && (
            <>
              {/* Calendar View Button */}
              {onCalendarClick && (
                <button
                  type="button"
                  onClick={e => {
                    e.stopPropagation()
                    onCalendarClick()
                  }}
                  className="shrink-0 cursor-pointer rounded-[10px] border border-[rgba(242,240,235,0.15)] bg-[rgba(242,240,235,0.05)] p-2.75 transition-colors hover:bg-[rgba(242,240,235,0.1)]"
                  aria-label="Open calendar"
                >
                  <Calendar className="w-[22px] h-[22px] text-primary" />
                </button>
              )}

              {/* Calendar Upload Button */}
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation()
                  setShareOpen(true)
                }}
                className="shrink-0 cursor-pointer rounded-[10px] border border-[rgba(242,240,235,0.15)] bg-[rgba(242,240,235,0.05)] p-2.75 transition-colors hover:bg-[rgba(242,240,235,0.1)]"
                aria-label="Share"
              >
                <Upload className="w-[22px] h-[22px] text-primary" />
              </button>

              {/* Three-dot Dropdown Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="shrink-0 cursor-pointer rounded-[10px] border border-[rgba(242,240,235,0.15)] bg-[rgba(242,240,235,0.05)] p-2.75 transition-colors hover:bg-[rgba(242,240,235,0.1)]"
                    aria-label="More options"
                  >
                    <MoreVertical className="w-[22px] h-[22px] text-primary" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-white/10 border-white/20">
                  {onEditClick && (
                    <DropdownMenuItem
                      onClick={e => {
                        e.stopPropagation()
                        onEditClick()
                      }}
                      className="text-[#F2F0EB] hover:bg-white/10 cursor-pointer focus:bg-white/10"
                    >
                      <Pencil className="w-4 h-4 mr-2" />
                      Edit
                    </DropdownMenuItem>
                  )}
                  {onDeleteClick && (
                    <DropdownMenuItem
                      onClick={e => {
                        e.stopPropagation()
                        onDeleteClick()
                      }}
                      className="text-red-400 hover:bg-red-400/10 cursor-pointer focus:bg-red-400/10"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}

          {/* Bookmark Button */}
          {onBookmarkClick && !isOwner && (
            <button
              type="button"
              onClick={e => {
                e.stopPropagation()
                onBookmarkClick()
              }}
              className="shrink-0 cursor-pointer rounded-[10px] border border-[rgba(242,240,235,0.15)] bg-[rgba(242,240,235,0.05)] p-2.75 transition-colors hover:bg-[rgba(242,240,235,0.1)]"
              aria-label={isBookmarked ? "Remove bookmark" : "Add bookmark"}
            >
              <Bookmark
                className={`w-[22px] h-[22px] ${isBookmarked ? "fill-white text-white" : "text-primary"}`}
              />
            </button>
          )}
        </div>
      </div>

      <div className="p-5 flex flex-col flex-1 min-h-0">
        {/* Title */}
        <h3 className="text-sm md:text-[18px] font-semibold leading-5.5 text-[#F2F0EB] line-clamp-1">
          {title}
        </h3>
        <div className="flex items-center text-xs md:text-[13px] gap-2.5 mt-2.5">
          <span
            className={`rounded-[5px] px-1.5 py-0.5 leading-5 text-connect-cream opacity-90 ${
              isPaid ? "bg-brand-terracotta" : "bg-brand-olive"
            }`}
          >
            {priceLabel}
          </span>
          <span className="leading-5 text-[#F2F0EB]">Private Session</span>
          <span className="leading-normal text-[#F2F0EB]">{durationLabel}</span>
        </div>
        {/* Creator Profile */}
        {creator && !isOwner && (
          <div className="flex items-center gap-2.5 my-5">
            <button
              type="button"
              onClick={e => {
                e.stopPropagation()
                navigate(`/profile/${creator.id}`)
              }}
              className="cursor-pointer rounded-full border border-white/20"
              aria-label={`View ${creator.name || creator.username}'s profile`}
            >
              <Avatar
                src={creator.profilePictureUrl ?? undefined}
                fallback={creator.name || creator.username || "?"}
                size="sm"
                className="w-7.5 h-7.5"
              />
            </button>
            <span className="text-sm font-semibold text-[#F2F0EB] leading-5.5">
              {creator.name || creator.username}
            </span>
            <span className="text-sm text-[#F2F0EB80] leading-5.5">
              {(creator.followers || 0) >= 1000
                ? `${((creator.followers || 0) / 1000).toFixed(1)}k`
                : creator.followers || 0}{" "}
              follower{(creator.followers || 0) !== 1 && "s"}
            </span>
          </div>
        )}
        {/* Description */}
        {description && !isOwner && (
          <p className="text-[13px] leading-5 text-[#F2F0EB] line-clamp-2 overflow-hidden">
            {description}
          </p>
        )}
        <div className="flex lg:flex-row flex-col justify-between lg:items-center gap-1 mt-auto">
          {/* Tags Row */}
          {!isOwner && (
            <div className="flex items-center overflow-x-auto gap-2.5 pt-5 lg:max-w-70">
              {tags && tags.length > 0 && (
                <>
                  {tags.slice(0, 3).map(tag => (
                    <TagChip
                      key={tag}
                      tag={tag}
                      tab="sessions"
                      className="border border-[#F2F0EB] rounded-lg p-2.5 text-[13px] leading-5 text-[#F2F0EB]"
                    />
                  ))}
                  {tags?.length > 3 && (
                    <span className="text-sm text-[#F2F0EB]">+{tags?.length - 3}</span>
                  )}
                </>
              )}
            </div>
          )}

          {/* Book a Session Button (non-owner view) */}
          {!isOwner && (
            <Button
              variant="outline"
              type="button"
              onClick={e => {
                e.stopPropagation()
                handleBookSessionClick()
              }}
              className="mt-4 h-[50px] w-full py-[15.5px] bg-transparent rounded-full flex items-center justify-center gap-2.5 transition-colors cursor-pointer border-[#F2F0EB] lg:max-w-[230px] w-full"
            >
              <Calendar className="w-4.5 h-4.5 text-[#F2F0EB]" />
              <span className="text-sm font-semibold text-[#F2F0EB] uppercase">Book a Session</span>
            </Button>
          )}
        </div>
      </div>

      {/* Owner stats - only show when real stats are available via props */}
      {isOwner && (
        <div className="p-5 bg-[#F2F0EB1A] shrink-0 hidden md:block">
          <div className="text-[16px] leading-normal text-[#F2F0EB99] flex flex-wrap gap-x-5 gap-y-1">
            <span>
              Views · <span className="font-semibold">{stats?.views ?? 0}</span>
            </span>
            <span>
              Bookings · <span className="font-semibold">{stats?.bookings ?? 0}</span>
            </span>
            <span>
              Completed · <span className="font-semibold">{stats?.completed ?? 0}</span>
            </span>
            <span>
              Earnings · <span className="font-semibold">${stats?.earning ?? 0}</span>
            </span>
          </div>
        </div>
      )}
      {isOwner && (
        <div className="p-5 bg-[#F2F0EB1A] shrink-0 block md:hidden">
          <div className="text-sm leading-normal text-[#F2F0EB99] flex flex-wrap gap-x-5 gap-y-1">
            <span className="flex items-center gap-2.5">
              <Eye className="w-5 h-5" />{" "}
              <span className="font-semibold text-primary">{stats?.views ?? 0}</span>
            </span>
            <span className="flex items-center gap-2.5">
              <Users className="w-5 h-5" />{" "}
              <span className="font-semibold text-primary">{stats?.bookings ?? 0}</span>
            </span>
            <span className="flex items-center gap-2.5">
              <CircleCheck className="w-5 h-5" />{" "}
              <span className="font-semibold text-primary">{stats?.completed ?? 0}</span>
            </span>
            <span className="flex items-center gap-2.5">
              <Banknote className="w-5 h-5" />{" "}
              <span className="font-semibold text-primary">${stats?.earning ?? 0}</span>
            </span>
          </div>
        </div>
      )}

      {/* Booking Modal */}
      {!isOwner && creator && (
        <CoachingBookSessionModal
          open={isBookingModalOpen}
          onOpenChange={setIsBookingModalOpen}
          session={{
            id: id,
            coverImage: coverImage ?? null,
            title: title,
            description: description ?? undefined,
            format: format as "IN_PERSON" | "VIRTUAL" | "HYBRID",
            duration: duration,
            durationType: durationType,
            price: price ?? null,
            participationFee: participationFee as "Free" | "Paid",
            websiteLink: websiteLink ?? null,
            tags: tags ?? [],
            streetAddress: location ?? null,
          }}
          expert={{
            id: creator.id,
            name: creator.name,
            username: creator.username,
            profilePictureUrl: creator.profilePictureUrl ?? null,
          }}
        />
      )}

      <ShareModal
        open={shareOpen}
        onOpenChange={setShareOpen}
        shareUrl={
          typeof window !== "undefined"
            ? `${window.location.origin}/coaching-sessions/${title.toLowerCase().replace(/\s+/g, "-")}`
            : ""
        }
        shareText={`Check out this coaching session: ${title}`}
        displayPath={`/coaching-sessions/${title.toLowerCase().replace(/\s+/g, "-")}`}
      />
    </div>
  )
}
